import { useEffect, useState, type ReactElement, type SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { getErrorCodeForUi } from '@/api/errors';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FormField } from '@/components/ui/form-field';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';

import type { TenantState, TenantViewModel, UpdateTenantInput } from '../api';

export function EditTenantDialog({
  tenant,
  onCancel,
  onSubmit,
  pending,
  submitError,
}: {
  readonly tenant: TenantViewModel | null;
  readonly onCancel: () => void;
  readonly onSubmit: (input: UpdateTenantInput) => void;
  readonly pending: boolean;
  readonly submitError: unknown;
}): ReactElement {
  const { t } = useTranslation();
  const open = tenant !== null;
  const [label, setLabel] = useState('');
  const [state, setState] = useState<TenantState>('active');

  // Re-seed local draft each time a different tenant opens the dialog.
  useEffect(() => {
    if (tenant !== null) {
      setLabel(tenant.label);
      setState(tenant.state);
    }
  }, [tenant]);

  function handleSubmit(event: SyntheticEvent): void {
    event.preventDefault();
    if (tenant === null) return;
    onSubmit({ uuid: tenant.uuid, label: label.trim(), state });
  }

  const submitErrorCode = getErrorCodeForUi(submitError);

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onCancel(); }}>
      <DialogContent disableOutsideClose>
        <DialogHeader>
          <DialogTitle>{t('routes.platform.tenants.edit.title')}</DialogTitle>
          <DialogDescription>{t('routes.platform.tenants.edit.description')}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} noValidate className="space-y-4">
          <FormField
            id="edit-tenant-name"
            label={t('routes.platform.tenants.edit.nameReadonly')}
          >
            {(fp) => (
              <Input
                {...fp}
                value={tenant?.name ?? ''}
                readOnly
                disabled
                className={`${fp.className} font-mono`}
              />
            )}
          </FormField>
          <FormField id="edit-tenant-label" label={t('routes.platform.tenants.create.fields.label')}>
            {(fp) => (
              <Input
                {...fp}
                value={label}
                onChange={(e) => { setLabel(e.target.value); }}
                autoComplete="off"
              />
            )}
          </FormField>
          <FormField id="edit-tenant-state" label={t('routes.platform.tenants.create.fields.state')}>
            {(fp) => (
              <Select
                {...fp}
                value={state}
                onChange={(e) => { setState(e.target.value as TenantState); }}
              >
                <option value="active">{t('routes.platform.tenants.state.active')}</option>
                <option value="inactive">{t('routes.platform.tenants.state.inactive')}</option>
              </Select>
            )}
          </FormField>
          {submitError !== undefined && submitError !== null && (
            <p role="alert" className="text-sm text-red-600">
              {t('routes.platform.tenants.edit.errors.submit', { code: submitErrorCode })}
            </p>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onCancel} disabled={pending}>
              {t('routes.platform.tenants.edit.cancel')}
            </Button>
            <Button type="submit" loading={pending}>
              {t('routes.platform.tenants.edit.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
